import { route, SandboxError, type Route } from '../adapter';
import { newId, now, type SandboxStore } from '../store';
import type { SandboxSeasonTag } from '../seed/portal';
import { BANNER_AUDIENCE, BANNER_STATUS, type PromoBanner, type PromoBannerInput } from '@/lib/promo-banners';
import type { ProgramPairing, ProgramPairingInput } from '@/lib/program-pairings';
import { MONTH_OPTIONS, type SeasonTag, type SeasonTagDraft } from '@/lib/season-tags';
import { todayIso } from '@/lib/dev-tracker';
import type { Program } from '@/lib/events';

/**
 * What Roy curates for every client: Overview banners, program pairings and
 * season tags, plus the program list their pickers read. Banner status and
 * the overlap count are worked out on every read, as the API does.
 */

/** Live, scheduled or ended, by today's date. */
function statusOf(banner: Pick<PromoBanner, 'startsOn' | 'endsOn'>, today: string): PromoBanner['status'] {
  if (banner.endsOn !== null && banner.endsOn < today) return BANNER_STATUS.ended;
  if (banner.startsOn !== null && banner.startsOn > today) return BANNER_STATUS.scheduled;
  return BANNER_STATUS.live;
}

function windowsOverlap(a: Pick<PromoBanner, 'startsOn' | 'endsOn'>, b: Pick<PromoBanner, 'startsOn' | 'endsOn'>): boolean {
  const aStart = a.startsOn ?? '0000-00-00';
  const bStart = b.startsOn ?? '0000-00-00';
  const aEnd = a.endsOn ?? '9999-12-31';
  const bEnd = b.endsOn ?? '9999-12-31';
  return aStart <= bEnd && bStart <= aEnd;
}

function audiencesMeet(a: PromoBanner, b: PromoBanner): boolean {
  if (a.audience === BANNER_AUDIENCE.allPortals || b.audience === BANNER_AUDIENCE.allPortals) return true;
  return a.organizations.some((organization) => b.organizations.some((other) => other.id === organization.id));
}

/** Every banner with its status and how many others could share a portal with it. */
export function bannersView(banners: readonly PromoBanner[], today: string = todayIso()): PromoBanner[] {
  const withStatus = banners.map((banner) => ({ ...banner, status: statusOf(banner, today) }));
  return withStatus.map((banner) => ({
    ...banner,
    overlapping:
      banner.status === BANNER_STATUS.ended
        ? 0
        : withStatus.filter((other) => other.id !== banner.id && other.status !== BANNER_STATUS.ended && windowsOverlap(banner, other) && audiencesMeet(banner, other)).length,
  }));
}

function bannerOf(store: SandboxStore, input: PromoBannerInput, existing?: PromoBanner): PromoBanner {
  if (input.headline.trim() === '') throw new SandboxError(400, 'Add a headline.');
  const organizations = input.audience === BANNER_AUDIENCE.organizations ? store.portal.organizations.filter((organization) => input.organizationIds.includes(organization.id)).map((organization) => ({ id: organization.id, name: organization.name })) : [];
  return {
    id: existing?.id ?? newId(),
    kind: input.kind,
    eyebrow: input.eyebrow,
    headline: input.headline,
    body: input.body,
    ctaLabel: input.ctaLabel,
    ctaHref: input.ctaHref,
    audience: input.audience,
    organizations,
    startsOn: input.startsOn || null,
    endsOn: input.endsOn || null,
    status: BANNER_STATUS.live,
    overlapping: 0,
    createdBy: existing?.createdBy ?? 'You',
    createdAt: existing?.createdAt ?? now(),
  };
}

function viewOfBanner(store: SandboxStore, id: string): PromoBanner {
  const found = bannersView(store.portal.banners).find((banner) => banner.id === id);
  if (!found) throw new SandboxError(404, 'Banner not found.');
  return found;
}

function pairingOf(store: SandboxStore, input: ProgramPairingInput, existing?: ProgramPairing): ProgramPairing {
  const side = (id: string, label: string): ProgramPairing['programA'] => ({ id, name: store.portal.programs.find((program) => program.id === id)?.name ?? null, label, isActive: true });
  return {
    id: existing?.id ?? newId(),
    occasion: input.occasion,
    reason: input.reason,
    isActive: input.isActive,
    programA: side(input.programAId, input.programALabel),
    programB: side(input.programBId, input.programBLabel),
    updatedAt: now(),
  };
}

/** "November to December", "April", "June to August": how the API words a tag's months. */
function monthsLabel(months: readonly number[]): string {
  const names = months.map((month) => MONTH_OPTIONS[month - 1]?.label ?? '');
  if (names.length === 0) return '';
  const contiguous = months.every((month, index) => index === 0 || month === (months[index - 1] ?? 0) + 1);
  if (names.length === 1) return names[0] ?? '';
  return contiguous ? `${names[0]} to ${names.at(-1)}` : names.join(', ');
}

function tagView(tag: SandboxSeasonTag): SeasonTag {
  const { programIds, ...rest } = tag;
  return { ...rest, monthsLabel: monthsLabel(tag.months), programCount: programIds.length };
}

function tagOr404(store: SandboxStore, id: string): SandboxSeasonTag {
  const found = store.portal.seasonTags.find((tag) => tag._id === id);
  if (!found) throw new SandboxError(404, 'Season tag not found.');
  return found;
}

/** The sample program carries the tags whose list holds it. */
function withSeasonTags(store: SandboxStore, program: Program): Program {
  return { ...program, seasonTagIds: store.portal.seasonTags.filter((tag) => tag.programIds.includes(program._id)).map((tag) => tag._id) };
}

export const curationRoutes: readonly Route[] = [
  route('GET', '/promo-banners', ({ store }) => ({ data: bannersView(store.portal.banners) })),
  route('POST', '/promo-banners', ({ store, body }) => {
    const banner = bannerOf(store, body as PromoBannerInput);
    store.portal.banners = [...store.portal.banners, banner];
    return { data: viewOfBanner(store, banner.id) };
  }),
  route('PUT', '/promo-banners/:id', ({ store, params, body }) => {
    const existing = store.portal.banners.find((banner) => banner.id === params['id']);
    if (!existing) throw new SandboxError(404, 'Banner not found.');
    const updated = bannerOf(store, body as PromoBannerInput, existing);
    store.portal.banners = store.portal.banners.map((banner) => (banner.id === updated.id ? updated : banner));
    return { data: viewOfBanner(store, updated.id) };
  }),
  route('DELETE', '/promo-banners/:id', ({ store, params }) => {
    store.portal.banners = store.portal.banners.filter((banner) => banner.id !== params['id']);
    return { data: null };
  }),

  // The catalog's program list, for the pairing pickers and Catalog → Programs.
  route('GET', '/programs', ({ store }) => {
    const sample = withSeasonTags(store, store.program);
    const rows: Program[] = [sample, ...store.portal.programs.filter((program) => program.name !== sample.name).map((program) => withSeasonTags(store, { _id: program.id, name: program.name, isActive: true }))];
    return { data: rows, pagination: { page: 1, limit: rows.length, total: rows.length, totalPages: 1, hasNext: false, hasPrev: false } };
  }),
  route('GET', '/programs/:id', ({ store, params }) => {
    if (params['id'] === store.programId) return { data: withSeasonTags(store, store.program) };
    const program = store.portal.programs.find((candidate) => candidate.id === params['id']);
    if (!program) throw new SandboxError(404, 'Program not found.');
    return { data: withSeasonTags(store, { _id: program.id, name: program.name, isActive: true }) };
  }),
  route('PUT', '/programs/:id', ({ store, params, body }) => {
    const id = params['id'] ?? '';
    const patch = body as Partial<Program>;
    if (patch.seasonTagIds) {
      const wanted = new Set(patch.seasonTagIds);
      store.portal.seasonTags = store.portal.seasonTags.map((tag) => ({ ...tag, programIds: wanted.has(tag._id) ? [...new Set([...tag.programIds, id])] : tag.programIds.filter((programId) => programId !== id) }));
    }
    if (id === store.programId) {
      const { seasonTagIds: _tags, ...rest } = patch;
      store.program = { ...store.program, ...rest };
      return { data: withSeasonTags(store, store.program) };
    }
    const program = store.portal.programs.find((candidate) => candidate.id === id);
    if (!program) throw new SandboxError(404, 'Program not found.');
    return { data: withSeasonTags(store, { _id: program.id, name: patch.name ?? program.name, isActive: true }) };
  }),

  route('GET', '/program-pairings', ({ store }) => ({ data: store.portal.pairings })),
  route('POST', '/program-pairings', ({ store, body }) => {
    const pairing = pairingOf(store, body as ProgramPairingInput);
    store.portal.pairings = [...store.portal.pairings, pairing];
    return { data: pairing };
  }),
  route('PATCH', '/program-pairings/:id', ({ store, params, body }) => {
    const existing = store.portal.pairings.find((pairing) => pairing.id === params['id']);
    if (!existing) throw new SandboxError(404, 'Pairing not found.');
    const current: ProgramPairingInput = { occasion: existing.occasion, reason: existing.reason, isActive: existing.isActive, programAId: existing.programA.id, programALabel: existing.programA.label, programBId: existing.programB.id, programBLabel: existing.programB.label };
    const updated = pairingOf(store, { ...current, ...(body as Partial<ProgramPairingInput>) }, existing);
    store.portal.pairings = store.portal.pairings.map((pairing) => (pairing.id === updated.id ? updated : pairing));
    return { data: updated };
  }),
  route('DELETE', '/program-pairings/:id', ({ store, params }) => {
    store.portal.pairings = store.portal.pairings.filter((pairing) => pairing.id !== params['id']);
    return { data: null };
  }),

  route('GET', '/season-tags', ({ store }) => ({ data: store.portal.seasonTags.map(tagView) })),
  route('GET', '/season-tags/:id/programs', ({ store, params }) => {
    const tag = tagOr404(store, params['id'] ?? '');
    const names = new Map([...store.portal.programs.map((program) => [program.id, program.name] as const), [store.programId, store.program.name ?? 'Program'] as const]);
    return { data: tag.programIds.map((id) => ({ id, name: names.get(id) ?? 'Program' })) };
  }),
  route('POST', '/season-tags', ({ store, body }) => {
    const draft = body as SeasonTagDraft & { description?: string };
    if (draft.name.trim() === '' || draft.months.length === 0) throw new SandboxError(400, 'Name the season and pick its months.');
    const tag: SandboxSeasonTag = { _id: newId(), name: draft.name.trim(), months: [...draft.months].sort((a, b) => a - b), description: draft.description ?? null, isActive: true, programIds: [] };
    store.portal.seasonTags = [...store.portal.seasonTags, tag];
    return { data: tagView(tag) };
  }),
  route('PATCH', '/season-tags/:id', ({ store, params, body }) => {
    const existing = tagOr404(store, params['id'] ?? '');
    const patch = body as Partial<SeasonTagDraft & { isActive: boolean }>;
    const updated: SandboxSeasonTag = {
      ...existing,
      ...(patch.name === undefined ? {} : { name: patch.name }),
      ...(patch.months === undefined ? {} : { months: [...patch.months].sort((a, b) => a - b) }),
      ...(patch.description === undefined ? {} : { description: patch.description || null }),
      ...(patch.isActive === undefined ? {} : { isActive: patch.isActive }),
    };
    store.portal.seasonTags = store.portal.seasonTags.map((tag) => (tag._id === updated._id ? updated : tag));
    return { data: tagView(updated) };
  }),
];
